import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Stack,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { AttachMoney as SalaryIcon, TrendingUp as IncreaseIcon } from '@mui/icons-material';
import { useUpdateSalary, useSalaryHistory } from '../hooks/useEmployee';
import { Employee } from '../types';

interface SalaryAdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  employee: Employee | null;
}

const SalaryAdjustmentDialog: React.FC<SalaryAdjustmentDialogProps> = ({
  open,
  onClose,
  employee,
}) => {
  const updateSalary = useUpdateSalary();
  const { data: salaryHistory, isLoading: historyLoading } = useSalaryHistory(
    employee?.id || 0,
    open && !!employee
  );

  const [formData, setFormData] = useState({
    new_salary: 0,
    effective_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (open) {
      setFormData({
        new_salary: 0,
        effective_date: new Date().toISOString().split('T')[0],
      });
    }
  }, [open]);

  const handleChange = (field: string) => (e: any) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee) return;

    try {
      await updateSalary.mutateAsync({
        id: employee.id,
        data: formData,
      });
      handleClose();
    } catch (error) {
      console.error('Failed to update salary:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      new_salary: 0,
      effective_date: new Date().toISOString().split('T')[0],
    });
    onClose();
  };

  const currentSalary = employee ? parseFloat(employee.salary.replace(/[^0-9.]/g, '')) : 0;
  const newSalary = formData.new_salary;
  const salaryDifference = newSalary - currentSalary;
  const percentageChange = currentSalary > 0 ? (salaryDifference / currentSalary) * 100 : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <SalaryIcon />
          <Typography variant="h6">Adjust Salary - {employee?.name}</Typography>
        </Stack>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Salary
                  </Typography>
                  <Typography variant="h5" color="primary">
                    ₵{currentSalary.toFixed(2)}
                  </Typography>
                </Box>

                <Divider />

                <TextField
                  fullWidth
                  label="New Salary"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={formData.new_salary}
                  onChange={handleChange('new_salary')}
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>₵</Typography>,
                  }}
                />

                <TextField
                  fullWidth
                  label="Effective Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.effective_date}
                  onChange={handleChange('effective_date')}
                  required
                />

                {newSalary > 0 && salaryDifference !== 0 && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: salaryDifference > 0 ? 'success.main' : 'error.main',
                      borderRadius: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <IncreaseIcon />
                      <Typography variant="h6" color="white">
                        {salaryDifference > 0 ? '+' : ''}₵{Math.abs(salaryDifference).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="white">
                        ({percentageChange > 0 ? '+' : ''}
                        {percentageChange.toFixed(1)}%)
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Box>

          {/* Salary History */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Salary History
            </Typography>
            {historyLoading ? (
              <Typography color="text.secondary">Loading history...</Typography>
            ) : salaryHistory && salaryHistory.length > 0 ? (
              <List>
                {salaryHistory.map((history) => (
                  <ListItem key={history.id} divider>
                    <ListItemText
                      primary={`${history.new_salary}${history.is_active ? ' (Current)' : ''}`}
                      secondary={`Effective: ${new Date(
                        history.effective_date
                      ).toLocaleDateString()}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary">No salary history available</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateSalary.isPending}
            sx={{
              background: 'linear-gradient(45deg, #ff9800 30%, #ffa726 90%)',
              borderRadius: '12px',
            }}
          >
            {updateSalary.isPending ? 'Updating...' : 'Update Salary'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SalaryAdjustmentDialog;
